import { IGetValueArgs } from "@components/smart/FieldInfo";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    IPrEmploymentEntity,
    PrEmploymentEntity,
    PrEmploymentInitialDataEntity,
    PrEmploymentInitialLongTermTimeBonusEntity,
    PrEmploymentInitialMonthDataEntity,
    PrInitialDataEntity,
    PrInitialMonthDataEntity
} from "@odata/GeneratedEntityTypes";
import { PrEmploymentCategoryCode, PrEmploymentTypeCode, PrTimeBasedBonusTypeCode } from "@odata/GeneratedEnums";
import { TFieldDefinition } from "@pages/PageUtils";
import { getEmployeeSummaryItemDef } from "@pages/payroll/employee/Employee.utils";
import { getPayrollCurrencyFormatter } from "@pages/payroll/payroll.utils";
import { timeStampToMinutesFormatter, timeStampToMinutesParser } from "@pages/payroll/payslip/Payslip.def";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { isNotDefined } from "@utils/general";
import { capitalize } from "@utils/string";
import i18next from "i18next";
import React from "react";

import { DASH_CHARACTER } from "../../../../constants";
import { BasicInputSizes, FieldType, GroupedField, LabelStatus, ValidatorType } from "../../../../enums";
import { formatCurrencyVariableDecimals } from "../../../../types/Currency";
import DateType, { DateFormat, getUtcDayjs } from "../../../../types/Date";
import { IFormDef } from "../../../../views/formView/Form";
import { FormTabs } from "../../../../views/formView/FormView.styles";
import {
    EmployeeInitialDataPath,
    EmployeeMonthDataPath,
    getActiveInitialDataTab,
    hasInitialDataTabError,
    IInitialDataLimits,
    InitialDataTab,
    isFirstPayrollInJanuary,
    isInitialDataMonthInPreviousCalendarYear,
    setActiveInitialDataTab,
    TEmploymentInitialDataStorage
} from "./EmploymentInitialData.utils";

export const VacationBalanceTypePath = BindingContext.localContext("VacationBalanceType");
export const SickDayBalanceTypePath = BindingContext.localContext("SickDayBalanceType");
export const SickDayVacationTransferredPlaceholderPath = BindingContext.localContext("SickDayVacationTransferredPlaceholder");

export const EmploymentMonthDataPath = createPath(PrEmploymentEntity.InitialData, PrEmploymentInitialDataEntity.EmploymentMonthData);
export const LongTermTimeBonusesPath = createPath(PrEmploymentEntity.InitialData, PrEmploymentInitialDataEntity.LongTermTimeBonuses);

const initialDataPath = (property: PrEmploymentInitialDataEntity): string => createPath(PrEmploymentEntity.InitialData, property);
const employeeInitialDataPath = (property: PrInitialDataEntity): string => createPath(EmployeeInitialDataPath, property);

const getActiveTab = (args: IGetValueArgs): InitialDataTab => getActiveInitialDataTab(args.storage as TEmploymentInitialDataStorage);
const isEmploymentTabActive = (args: IGetValueArgs): boolean => getActiveTab(args) === InitialDataTab.Employment;
const isCommonTabActive = (args: IGetValueArgs): boolean => getActiveTab(args) === InitialDataTab.Common;

const nonNegativeNumberValidator = {
    type: ValidatorType.Number,
    settings: {
        min: 0
    }
};

const getHoursFieldDef = (label: string) => ({
    type: FieldType.NumberInput,
    label: i18next.t(`Employee:InitialData.${label}`),
    fieldSettings: {
        formatter: timeStampToMinutesFormatter,
        parser: timeStampToMinutesParser,
        unit: i18next.t("Common:General.Hour")
    },
    validator: nonNegativeNumberValidator,
    width: BasicInputSizes.M
});

const getDaysFieldDef = (label: string) => ({
    type: FieldType.NumberInput,
    label: i18next.t(`Employee:InitialData.${label}`),
    fieldSettings: {
        unit: i18next.t("Common:General.Day")
    },
    validator: nonNegativeNumberValidator,
    width: BasicInputSizes.M
});

const getCurrencyFieldDef = (label: string, currencyUnit: string) => ({
    type: FieldType.NumberInput,
    label: i18next.t(`Employee:InitialData.${label}`),
    formatter: getPayrollCurrencyFormatter(),
    fieldSettings: {
        unit: currencyUnit
    },
    validator: nonNegativeNumberValidator,
    width: BasicInputSizes.M
});

const employeeMonthlyFields = [PrInitialMonthDataEntity.DateMonth, PrInitialMonthDataEntity.IncomeTaxBasis,
    PrInitialMonthDataEntity.AdvanceTaxPaid, PrInitialMonthDataEntity.IncomeTaxBonus, PrInitialMonthDataEntity.WithholdingTaxPaid];


export function getEmploymentInitialDataFormDef(employment: IPrEmploymentEntity, currencyUnit: string, isReadOnly: boolean): IFormDef {
    const dateMonthFieldDef: TFieldDefinition = {
        type: FieldType.Input,
        isDisabled: true,
        label: i18next.t("Employee:InitialData.Month"),
        width: BasicInputSizes.S,
        formatter: value => value ? capitalize(DateType.format(value as Date, DateFormat.month)) : ""
    };

    const isDpp = employment.TypeCode === PrEmploymentTypeCode.WorkAgreementDPP;
    const isDpc  = employment.TypeCode === PrEmploymentTypeCode.WorkAgreementDPC;
    const isStatutoryWithCompensation = employment.EmploymentCategoryCode === PrEmploymentCategoryCode.Associate
        && employment.TypeCode === PrEmploymentTypeCode.Compensation;
    const isVacationSectionVisible = (args: IGetValueArgs): boolean => !isStatutoryWithCompensation && isEmploymentTabActive(args);
    const hasSickDays = (employment.CurrentTemporalPropertyBag?.SickDays ?? 0) > 0;
    const isCommonTabVisible = !isFirstPayrollInJanuary(employment);
    const employmentTabTitle = [employment.Type?.Name ?? employment.TypeCode, employment.NumberOurs].filter(Boolean).join(" - ")
        + (employment.Name ? ` (${employment.Name})` : "");
    const longTermTimeBonusMinDate = getUtcDayjs(employment.DateStart).startOf("month").toDate();
    const longTermTimeBonusMaxDate = getUtcDayjs(employment.DateCalculatePayslipStart).subtract(1, "month").endOf("month").toDate();
    const ongoingIllnessMaxDate = getUtcDayjs(employment.DateCalculatePayslipStart).subtract(1, "day").toDate();
    const getMonthlyRow = ({ storage, bindingContext }: IGetValueArgs): Record<string, unknown> => {
        if (!storage || !bindingContext) {
            return null;
        }

        return storage.getValue(bindingContext.getParent()) as Record<string, unknown>;
    };
    const isMonthlyTaxFieldReadOnly = (args: IGetValueArgs): boolean => {
        return isInitialDataMonthInPreviousCalendarYear(getMonthlyRow(args)?.[PrInitialMonthDataEntity.DateMonth] as Date, employment);
    };
    const isMonthlyTaxFieldRequired = (args: IGetValueArgs): boolean => {
        return !isMonthlyTaxFieldReadOnly(args);
    };
    const getMonthlyTaxFieldDef = (label: string) => ({
        ...getCurrencyFieldDef(label, currencyUnit),
        isReadOnly: isMonthlyTaxFieldReadOnly,
        isRequired: isMonthlyTaxFieldRequired
    });
    const getBalanceTypeFieldDef = (value: string, labelStatus?: LabelStatus) => ({
        type: FieldType.Input,
        isDisabled: true,
        label: i18next.t("Employee:InitialData.BalanceType"),
        labelStatus,
        width: BasicInputSizes.M,
        formatter: () => value
    });
    const getBalancePlaceholderFieldDef = (isVisible: boolean) => ({
        type: FieldType.Input,
        isDisabled: true,
        isVisible,
        labelStatus: LabelStatus.Removed,
        width: BasicInputSizes.M
    });
    const getCommonFieldDef = (label: keyof IInitialDataLimits) => ({
        ...getCurrencyFieldDef(label, currencyUnit),
        isRequired: isCommonTabVisible,
        width: BasicInputSizes.XL,
        fieldSettings: {
            unit: ({ storage }: IGetValueArgs) => {
                const limit = (storage as TEmploymentInitialDataStorage).getCustomData().commonFieldLimits?.[label];
                const formattedLimit = isNotDefined(limit) ? DASH_CHARACTER : formatCurrencyVariableDecimals(limit, getCompanyCurrency(storage.context));
                return `${currencyUnit} / ${formattedLimit}`;
            }
        }
    });

    return {
        id: "EmploymentInitialDataForm",
        title: i18next.t("Employee:InitialData.Title"),
        summary: [{
            ...getEmployeeSummaryItemDef(PrEmploymentEntity.Employee, true),
            label: ""
        }, {
            id: PrEmploymentEntity.DateCalculatePayslipStart,
            label: i18next.t("Employee:InitialData.FirstEvalaPayroll"),
            formatter: value => value ? capitalize(DateType.format(value as Date, DateFormat.monthAndYear)) : ""
        }],
        isReadOnly,
        translationFiles: ["Employee", "Common", "SalaryComponent"],
        fieldDefinition: {
            // EMPLOYMEE MONTH DATA
            [createPath(EmployeeMonthDataPath, PrInitialMonthDataEntity.DateMonth)]: {
                ...dateMonthFieldDef
             },
            [createPath(EmployeeMonthDataPath, PrInitialMonthDataEntity.IncomeTaxBasis)]: {
                ...getMonthlyTaxFieldDef("IncomeTaxBasis"),
                width: BasicInputSizes.S
            },
            [createPath(EmployeeMonthDataPath, PrInitialMonthDataEntity.AdvanceTaxPaid)]: {
                ...getMonthlyTaxFieldDef("AdvanceTaxPaid"),
                width: BasicInputSizes.S
            },
            [createPath(EmployeeMonthDataPath, PrInitialMonthDataEntity.IncomeTaxBonus)]: {
                ...getMonthlyTaxFieldDef("IncomeTaxBonus"),
                width: BasicInputSizes.S
            },
            [createPath(EmployeeMonthDataPath, PrInitialMonthDataEntity.WithholdingTaxPaid)]: {
                ...getMonthlyTaxFieldDef("WithholdingTaxPaid"),
                width: BasicInputSizes.S
            },
            

            // EMPLOYMENT MONTH DATA
            [createPath(EmploymentMonthDataPath, PrEmploymentInitialMonthDataEntity.DateMonth)]: {
               ...dateMonthFieldDef
            },
            [createPath(EmploymentMonthDataPath, PrEmploymentInitialMonthDataEntity.ExcludedDays)]: {
                ...getDaysFieldDef("ExcludedDays"),
                isRequired: true,
                defaultValue: 0,
                validator: {
                    type: ValidatorType.Integer,
                    settings: { min: 0 }
                }
            },
            [createPath(EmploymentMonthDataPath, PrEmploymentInitialMonthDataEntity.SocialSecurityInsuranceBasis)]: {
                ...getCurrencyFieldDef("SocialSecurityInsuranceBasis", currencyUnit),
                label: i18next.t("Employee:InitialData.SocialSecurityInsuranceBasisShort"),
                width: BasicInputSizes.S
            },
            //
            [initialDataPath(PrEmploymentInitialDataEntity.AverageSalaryLastQuarter)]: getCurrencyFieldDef("AverageSalaryLastQuarter", currencyUnit),
            [createPath(LongTermTimeBonusesPath, PrEmploymentInitialLongTermTimeBonusEntity.DateMonth)]: {
                type: FieldType.MonthYear,
                isRequired: true,
                label: i18next.t("Employee:InitialData.PaidInMonth"),
                defaultValue: () => longTermTimeBonusMinDate,
                fieldSettings: {
                    previewValue: longTermTimeBonusMinDate,
                    minDate: longTermTimeBonusMinDate,
                    maxDate: longTermTimeBonusMaxDate
                }
            },
            [createPath(LongTermTimeBonusesPath, PrEmploymentInitialLongTermTimeBonusEntity.TimeBasedBonusType)]: {
                type: FieldType.ComboBox,
                isRequired: true,
                label: i18next.t("Employee:InitialData.Period"),
                fieldSettings: {
                    displayName: "Name",
                    itemsForRender: () => [{
                        id: PrTimeBasedBonusTypeCode.Halfyearly,
                        label: i18next.t("SalaryComponent:TimeBasedBonusType.HalfYearly")
                    }, {
                        id: PrTimeBasedBonusTypeCode.Annual,
                        label: i18next.t("SalaryComponent:TimeBasedBonusType.Yearly")
                    }]
                }
            },
            [createPath(LongTermTimeBonusesPath, PrEmploymentInitialLongTermTimeBonusEntity.Amount)]: {
                ...getCurrencyFieldDef("Amount", currencyUnit),
                isRequired: true,
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0,
                        excludeMin: true
                    }
                }
            },
            [initialDataPath(PrEmploymentInitialDataEntity.VacationTransferredFromLastYearInMinutes)]: getHoursFieldDef("VacationTransferredFromLastYear"),
            [initialDataPath(PrEmploymentInitialDataEntity.VacationDrawdownThisYearInMinutes)]: {
                ...getHoursFieldDef("VacationDrawdownThisYear"),
                groupedField: GroupedField.MultiEnd
            },
            [initialDataPath(PrEmploymentInitialDataEntity.FullyCountableTowardVacationInMinutes)]: getHoursFieldDef("FullyCountableTowardVacation"),
            [initialDataPath(PrEmploymentInitialDataEntity.PartiallyCountableTowardVacationInMinutes)]: getHoursFieldDef("PartiallyCountableTowardVacation"),
            [initialDataPath(PrEmploymentInitialDataEntity.SickDaysDrawdownThisYear)]: {
                ...getDaysFieldDef("SickDaysDrawdownThisYear"),
                labelStatus: LabelStatus.Removed,
                isVisible: hasSickDays,
                groupedField: GroupedField.MultiEnd
            },
            [VacationBalanceTypePath]: {
                ...getBalanceTypeFieldDef(i18next.t("Employee:InitialData.Vacation")),
                groupedField: GroupedField.MultiStart
            },
            [SickDayBalanceTypePath]: {
                ...getBalanceTypeFieldDef(i18next.t("Employee:InitialData.SickDay"), LabelStatus.Removed),
                groupedField: GroupedField.MultiStart,
                isVisible: hasSickDays
            },
            [SickDayVacationTransferredPlaceholderPath]: getBalancePlaceholderFieldDef(hasSickDays),
            [initialDataPath(PrEmploymentInitialDataEntity.OvertimePreviousInMinutes)]: {
                ...getHoursFieldDef("OvertimePrevious"),
                isVisible: !isDpp && !isDpc
            },
            [initialDataPath(PrEmploymentInitialDataEntity.HolidayPreviousInMinutes)]: getHoursFieldDef("HolidayPrevious"),
            [initialDataPath(PrEmploymentInitialDataEntity.DppWorkedFromLimitInMinutes)]: {
                ...getHoursFieldDef("DppWorkedFromLimit"),
                isVisible: isDpp,
                validator: {
                    type: ValidatorType.Number,
                    settings: { min: 0, max: 300 * 60 }
                }
            },
            [employeeInitialDataPath(PrInitialDataEntity.SavingForRetirementTotal)]: getCommonFieldDef("SavingForRetirementTotal"),
            [employeeInitialDataPath(PrInitialDataEntity.LeisureTimeBenefits)]: getCommonFieldDef("LeisureTimeBenefits"),
            [employeeInitialDataPath(PrInitialDataEntity.HealthBenefits)]: getCommonFieldDef("HealthBenefits")
        },
        groups: [{
            id: "Tabs",
            render: (storage: TEmploymentInitialDataStorage) => <FormTabs
                isFirst
                key="Tabs"
                data={[{
                    id: InitialDataTab.Employment,
                    title: employmentTabTitle,
                    hasError: hasInitialDataTabError(storage, InitialDataTab.Employment)
                }, ...(isCommonTabVisible ? [{
                    id: InitialDataTab.Common,
                    title: i18next.t("Employee:InitialData.Common"),
                    hasError: hasInitialDataTabError(storage, InitialDataTab.Common)
                }] : [])]}
                onChange={tabId => setActiveInitialDataTab(storage, tabId as InitialDataTab)}
                selectedTabId={getActiveInitialDataTab(storage)}/>
        }, {
            id: "MonthlyData",
            title: i18next.t("Employee:InitialData.MonthlyData"),
            isVisible: isEmploymentTabActive,
            showGroupDivider: false,
            rows: [[{ id: initialDataPath(PrEmploymentInitialDataEntity.DppWorkedFromLimitInMinutes) }]],
            lineItems: {
                collection: EmploymentMonthDataPath,
                columns: [
                    { id: PrEmploymentInitialMonthDataEntity.DateMonth },
                    { id: PrEmploymentInitialMonthDataEntity.SocialSecurityInsuranceBasis },
                    { id: PrEmploymentInitialMonthDataEntity.ExcludedDays }
                ],
                canAdd: false,
                canReorder: false,
                isItemCloneable: false,
                isItemRemovable: false,
                order: PrEmploymentInitialMonthDataEntity.DateMonth
            }
        }, {
            id: "AverageSalary",
            title: i18next.t("Employee:InitialData.AverageSalary"),
            isVisible: isEmploymentTabActive,
            showGroupDivider: false,
            rows: [[{ id: initialDataPath(PrEmploymentInitialDataEntity.AverageSalaryLastQuarter) }]]
        }, {
            id: "LongTermTimeBonuses",
            title: i18next.t("Employee:InitialData.LongTermBenefits"),
            isVisible: isEmploymentTabActive,
            showGroupDivider: false,
            rows: [],
            lineItems: {
                collection: LongTermTimeBonusesPath,
                columns: [
                    { id: PrEmploymentInitialLongTermTimeBonusEntity.DateMonth },
                    { id: PrEmploymentInitialLongTermTimeBonusEntity.TimeBasedBonusType },
                    { id: PrEmploymentInitialLongTermTimeBonusEntity.Amount }
                ],
                canAdd: true,
                canReorder: false,
                isItemCloneable: false,
                isTheOnlyItemRemovable: true,
                order: PrEmploymentInitialLongTermTimeBonusEntity.DateMonth,
                preserveOrderOnRemove: true
            }
        }, {
            id: "Balances",
            title: i18next.t("Employee:InitialData.Balances"),
            isVisible: isVacationSectionVisible,
            showGroupDivider: false,
            rows: [[
                { id: VacationBalanceTypePath },
                { id: initialDataPath(PrEmploymentInitialDataEntity.VacationTransferredFromLastYearInMinutes) },
                { id: initialDataPath(PrEmploymentInitialDataEntity.VacationDrawdownThisYearInMinutes) }
            ], [
                { id: SickDayBalanceTypePath },
                { id: SickDayVacationTransferredPlaceholderPath },
                { id: initialDataPath(PrEmploymentInitialDataEntity.SickDaysDrawdownThisYear) }
            ]]
        }, {
            id: "VacationEntitlementCalculation",
            title: i18next.t("Employee:InitialData.VacationEntitlementCalculation"),
            isVisible: isVacationSectionVisible,
            showGroupDivider: false,
            rows: [[
                { id: initialDataPath(PrEmploymentInitialDataEntity.FullyCountableTowardVacationInMinutes) },
                { id: initialDataPath(PrEmploymentInitialDataEntity.PartiallyCountableTowardVacationInMinutes) }
            ]]
        }, {
            id: "CompensatoryTime",
            title: i18next.t("Employee:InitialData.CompensatoryTime"),
            isVisible: isVacationSectionVisible,
            showGroupDivider: false,
            rows: [[
                { id: initialDataPath(PrEmploymentInitialDataEntity.OvertimePreviousInMinutes) },
                { id: initialDataPath(PrEmploymentInitialDataEntity.HolidayPreviousInMinutes) }
            ]]
        },
        {
            id: "CommonAnnualSettlement",
            isVisible: args => isCommonTabVisible && isCommonTabActive(args),
            showGroupDivider: false,
            lineItems: {
                collection: EmployeeMonthDataPath,
                columns: [
                  ...employeeMonthlyFields.map(field => ({ id: field }))
                ],
                canAdd: false,
                canReorder: false,
                isItemCloneable: false,
                isItemRemovable: false,
                order: PrInitialMonthDataEntity.DateMonth
            }
        },
        {
            id: "Common",
            title: i18next.t("Employee:InitialData.AnnualLimits"),
            isVisible: args => isCommonTabVisible && isCommonTabActive(args),
            showGroupDivider: false,
            rows: [[
                { id: employeeInitialDataPath(PrInitialDataEntity.HealthBenefits) },
                { id: employeeInitialDataPath(PrInitialDataEntity.LeisureTimeBenefits) },
                { id: employeeInitialDataPath(PrInitialDataEntity.SavingForRetirementTotal) }
            ]]
        }
    
    ]
    };
}
